<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="createVisible"
    v-loading="createProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="create-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Create Voicemail</h1>
        <div class="modal-actions col-2">
          <el-button
            type="primary"
            :disabled="numInUse"
            @click="createOperation"
          >
            Create
          </el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
            >Cancel</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          />
        </div>
      </div>
    </div>

    <el-form>
      <el-checkbox v-model="createForm.attach">Enable email delivery</el-checkbox>
      <email-phi-warning v-if="createForm.attach" />
      <el-checkbox v-if="createForm.attach" v-model="createForm.deleteVoicemail">
        Enable storing voicemails
      </el-checkbox>
      <el-tag class="mb-10" v-if="createForm.attach">
        <i class="el-icon-info" style="margin-right: 4px;" />
        This allows continued storage of voicemails after successful delivery
      </el-tag>
      <el-checkbox v-model="createForm.groupMailbox">Group mailbox</el-checkbox>
      <el-checkbox v-model="createForm.refillAttachments">Enable patient refill attachments</el-checkbox>
      <el-checkbox v-model="createForm.transcription" @change="onTranscriptionChange">Enable voicemail transcription</el-checkbox>
      <el-checkbox v-model="createForm.attachRecording">Enable attach voicemail recording</el-checkbox>
      <el-checkbox v-model="createForm.attachTranscription" @change="onAttachTranscriptionChange">Enable attach voicemail transcription</el-checkbox>
      <el-checkbox v-model="createForm.hipaa">Require message ID for message retrieval</el-checkbox>
      <el-checkbox v-model="createForm.disableDelete">Disable message deletion by phone</el-checkbox>
      <div class="inline-form-group">
        <el-col :span="11">
          <el-input v-model="createForm.fullName" placeholder="Full name">
            <template slot="prefix">Full name</template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="12">
          <el-input v-model="mailbox" placeholder="Enter mailbox number">
            <template slot="prefix">Mailbox number</template>
            <template slot="append" v-if="numInUse">In use!</template>
          </el-input>
        </el-col>
      </div>
      <div class="inline-form-group">
        <el-col :span="11">
          <el-input v-model="createForm.email" placeholder="Enter email">
            <template slot="prefix">Email</template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="12">
          <el-input v-model="createForm.password" placeholder="Enter pin">
            <template slot="prefix">Pin</template>
          </el-input>
        </el-col>
      </div>

      <div class="inline-form-group">
        <el-col :span="11">
          <dads-select
            v-model="createForm.branchId"
            :value="createForm.branchId"
            placeholder="Select branch"
            filterable
          >
            <template slot="prefix">Branch</template>
            <el-option
              v-for="(item, index) in branchOptions"
              :key="index"
              :label="item.name"
              :value="item.id"
            />
          </dads-select>
        </el-col>
      </div>

      <h2 class="form-section-header">
        Call Notifications
      </h2>
      <div class="inline-form-group">
        <el-col :span="11">
          <el-input v-model="createForm.notifyNumber" placeholder="Enter number">
            <template slot="prefix">Notify number</template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="12">
          <dads-select
            v-model="createForm.notifyCidnum"
            :value="createForm.notifyCidnum"
            clearable
            filterable
            remote
            :remote-method="remoteMethodForNotifyCid"
            placeholder="Select caller id"
          >
            <template slot="prefix">Caller id</template>
            <el-option
              v-for="option in cidNumOptions"
              :key="option.id"
              :value="option.label"
              :label="option.label | formatNumber"
            />
          </dads-select>
        </el-col>
      </div>

      <h2 class="form-section-header">Voicemail Length</h2>
      <div class="mb-30 voicemail-length-slider">
        <vue-slider
          v-model="createForm.voicemailLength"
          :min="2"
          :max="300"
          :interval="1"
          :dot-size="20"
          :width="300"
          :lazy="true"
          :tooltip="'always'"
          :piecewise="true"
          :piecewise-label="true"
          :marks="[2,300]"
          :process-style="{ 'backgroundColor': '#222' }"
          :dot-style="{ 'borderColor': '#222', 'backgroundColor': '#fff' }"
          :range="true"
        />
        <div class="mt-10">
          <span>Selected length: {{ createForm.voicemailLength ? createForm.voicemailLength.join('-') : '2-300' }} seconds</span>
        </div>
      </div>

      <h2 class="form-section-header">
        Virtual receptionist settings
      </h2>
      <div class="m-5">
        <el-checkbox v-model="allowIvr"
          >Allow virtual receptionist options</el-checkbox
        >
        <dads-select
          v-if="allowIvr"
          v-model="createForm.ivrId"
          :filterable="true"
          placeholder="Select virtual receptionist"
        >
          <template slot="prefix">Virtual receptionist</template>
          <el-option
            v-for="(ivr, i) of ivrOptions"
            :key="i"
            :label="ivr.name"
            :value="ivr.id"
          />
        </dads-select>
      </div>

      <!-- ⭐ OPERATOR DESTINATION ⭐ -->
      <h2 class="form-section-header">
        If the operator is dialed
      </h2>
      <destination-selector
        :isTerminalDestSelector="true"
        :draggable="true"
        :showHeader="false"
        :draggableDestTypes="draggableDestTypes"
        :selectedDestinations="createForm.operatorDestinations"
        @destinationsSelected="onOperatorDestinationsSelection"
        title=""
        :clearFields="clearFormFields"
      />
      <el-tag class="mb-10">
        <i class="el-icon-info" style="margin-right: 4px;" />
        The operator can be dialed by pressing the 0 key
      </el-tag>
    </el-form>
  </el-dialog>
</template>

<script>
import DestinationSelector from "@/components/DestinationSelector.vue";
import DadsSelect from "@/components/select";
import EmailPhiWarning from "@/components/EmailPhiWarning.vue";
import feathersClient from "@/feathers/feathers-client";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";

export default {
  name: "VoicemailCreateForm",

  props: {
    createVisible: { required: true },
    clearFormFields: { required: true },
    createProcessing: { required: true },
    numInUse: { required: true },
    ivrOptions: { required: true },
    draggableDestTypes: { required: true },
    cidNumOptions: { required: true, default: () => [] },
    branchOptions: { required: true }
  },

  components: {
    DestinationSelector,
    DadsSelect,
    EmailPhiWarning,
    VueSlider
  },

  data() {
    return {
      allowIvr: false,
      mailbox: "",
      createForm: {
        tenantId: null,
        context: "",
        tz: "",
        fullName: "",
        mailbox: "",
        email: "",
        password: "",
        attach: true,
        operatorDestinations: [],
        deleteVoicemail: true,
        ivrId: null,
        groupMailbox: false,
        notifyNumber: "",
        notifyCidnum: "",
        branchId: null,
        refillAttachments: false,
        transcription: false,
        attachRecording: false,
        attachTranscription: false,
        hipaa: 0,
        disableDelete: 0,
        voicemailLength: [2, 120]
      }
    };
  },
  async created(){
    await this.getDefaultBranch();
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    }
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    mailbox(val) {
      if (val) {
        this.createForm.mailbox = val;
        this.$emit("checkNumber", val);
      }
    }
  },

  methods: {
    // Attaching a transcript requires transcription; turning transcription off clears the attach
    onAttachTranscriptionChange(val) {
      if (val) this.createForm.transcription = true;
    },
    onTranscriptionChange(val) {
      if (!val) this.createForm.attachTranscription = false;
    },
    checkNumber(number) {
      this.number = number;
    },

    onOperatorDestinationsSelection(destinations) {
      this.createForm.operatorDestinations = [...destinations];
    },

    remoteMethodForNotifyCid(query) {
      this.$emit("fetchCidnumOptions", query);
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find(dst => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Don't leave any destination field to be empty.",
          type: "error"
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order
        }));
      }
      return finalDestinations;
    },

    createOperation() {
      this.createForm.tenantId = this.selectedTenant.id;
      this.createForm.context = this.selectedTenant.tenantCode;
      this.createForm.tz = this.selectedTenant.timezone.name;

      const createBody = { ...this.createForm };
      createBody.attach = this.createForm.attach ? "yes" : "no";
      createBody.deleteVoicemail =
        this.createForm.attach && this.createForm.deleteVoicemail
          ? "no"
          : "yes";

      createBody.groupMailbox = this.createForm.groupMailbox ? 1 : 0;
      createBody.refillAttachments = this.createForm.refillAttachments ? 1 : 0;
      createBody.transcription = this.createForm.transcription ? 1 : 0;
      createBody.attachRecording = this.createForm.attachRecording ? 1 : 0;
      createBody.attachTranscription = this.createForm.attachTranscription ? 1 : 0;
      createBody.hipaa = this.createForm.hipaa ? 1 : 0;
      createBody.disableDelete = this.createForm.disableDelete ? 1 : 0;

      //force storage to be on if email is not enabled
      if(createBody.attach === 'no'){
        createBody.deleteVoicemail = 'no';
      }

      if (this.allowIvr && this.createForm.ivrId) {
        createBody.ivrId = this.createForm.ivrId;
      } else {
        createBody.ivrId = 0;
      }

      if (
        Array.isArray(this.createForm.voicemailLength) &&
        this.createForm.voicemailLength.length === 2
      ) {
        createBody.minLength = this.createForm.voicemailLength[0];
        createBody.maxLength = this.createForm.voicemailLength[1];
      } else {
        createBody.minLength = 2;
        createBody.maxLength = 120;
      }

      const operatorDestinations = this.validateDestinations(
        createBody.operatorDestinations,
        "OPERATOR"
      );

      if (operatorDestinations) {
        createBody.operatorDestinations = operatorDestinations;
      } else {
        return;
      }

      this.$emit("createOperation", createBody);
    },
    async getDefaultBranch() {
      try {
        const [defaultBranch] = await feathersClient.service("branches").find({
          query: {
            tenantId: this.selectedTenant.id,
            default: 1
          }
        });
        this.createForm.branchId = defaultBranch.id;
      } catch (error) {
        console.log(error)
      }
    },

    cancelOperation() {
      this.$emit("cancelOperation", "create");
    },

    clearFields() {
      this.allowIvr = false;
      this.mailbox = "";
      this.createForm = {
        tenantId: null,
        context: "",
        tz: "",
        fullName: "",
        mailbox: "",
        email: "",
        password: "",
        attach: true,
        operatorDestinations: [],
        deleteVoicemail: true,
        ivrId: null,
        groupMailbox: false,
        branchId: null,
        refillAttachments: false,
        transcription: false,
        attachRecording: false,
        attachTranscription: false,
        hipaa: 0,
        disableDelete: 0,
        voicemailLength: [2, 120]
      };
      this.getDefaultBranch();
    },
  },

  filters: {
    formatNumber(value) {
      return value
        ? value.replace(/(\d{3})(\d{3})(\d{4})/g, "($1) $2-$3")
        : value;
    },
  },
};
</script>

<style lang="scss" scoped>
.el-form {
  max-width: 600px;
  margin: 20px auto 0 auto;

  .el-checkbox {
    margin-left: 0;
  }

  h4 {
    margin: 12px 0 0 3px;
  }
}

.el-date-editor {
  width: 100%;
}

.voicemail-length-slider {
  margin-bottom: 32px;
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  align-items: left;

  .vue-slider {
    margin-top: 16px;
    margin-bottom: 8px;
    margin-left: 16px;
    width: 320px;
    padding-left: 24px;
    padding-right: 24px;
  }
}
.vue-slider-dot-tooltip-inner {
  background: #222 !important;
  border-color: #fff !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  font-weight: bold;
  font-size: 14px;
}
</style>
